import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM, ColorSystem } from "components/design/design-system";
import { SubscriptionStatus } from "common/models/types";
import { AdminSubscriptionRow } from "common/types/adminSubscription";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import Spinner from "components/atoms/Spinner";
import Tag from "components/atoms/Tag";
import { Flex } from "components/atoms";
import { formatKstDate, formatKrw } from "utils/subscriptionDisplay";

interface SubscriptionTableProps {
  rows: AdminSubscriptionRow[];
  selectedId: number | null;
  onViewRow: (row: AdminSubscriptionRow) => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

// 상태 → 라벨/색. comp는 별도 배지로 표기하므로 여기선 status만.
export const STATUS_META: Record<SubscriptionStatus, { label: string; color: ColorSystem }> = {
  NONE: { label: "없음", color: "Gray" },
  PENDING: { label: "대기", color: "Yellow" },
  ACTIVE: { label: "활성", color: "Green" },
  PAST_DUE: { label: "연체(레거시)", color: "Orange" },
  CANCELLING: { label: "해지중", color: "Orange" },
  CANCELLED: { label: "해지", color: "Gray" },
  EXPIRED: { label: "만료", color: "Red" },
};

const TableContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: 8px 8px 0 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const Tr = styled.tr<{ $isSelected?: boolean }>`
  border-bottom: 1px solid #e5e7eb;
  background: ${(props) => (props.$isSelected ? "#f0f9ff" : "transparent")};
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: ${(props) => (props.$isSelected ? "#e0f2fe" : "#f9fafb")};
    cursor: pointer;
  }
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #374151;
  white-space: nowrap;
`;

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
  border-top: 1px solid #e5e7eb;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 16px;
  text-align: center;
`;

const SubscriptionTable: React.FC<SubscriptionTableProps> = ({
  rows,
  selectedId,
  onViewRow,
  hasMore,
  isLoadingMore,
  onLoadMore,
}) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastItemRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (!hasMore || !lastItemRef.current) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) onLoadMore();
      },
      { threshold: 0.5 }
    );
    observerRef.current.observe(lastItemRef.current);
    return () => observerRef.current?.disconnect();
  }, [rows, hasMore, isLoadingMore, onLoadMore]);

  if (rows.length === 0) {
    return (
      <TableContainer>
        <EmptyState>
          <Text textType="H4" style={{ marginBottom: "8px" }}>
            구독자가 없습니다
          </Text>
          <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[600]}>
            필터를 변경하거나 무료 제공으로 구독을 부여해보세요.
          </Text>
        </EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <TableWrapper>
        <Table>
          <Thead>
            <tr>
              <Th>이름</Th>
              <Th>이메일</Th>
              <Th>상태</Th>
              <Th>요금제</Th>
              <Th>금액</Th>
              <Th>다음 결제일</Th>
              <Th>가입일</Th>
            </tr>
          </Thead>
          <tbody>
            {rows.map((row, index) => {
              const isLastItem = index === rows.length - 1;
              const meta = STATUS_META[row.status] ?? STATUS_META.NONE;
              return (
                <Tr
                  key={row.id}
                  ref={isLastItem ? lastItemRef : undefined}
                  $isSelected={selectedId === row.id}
                  onClick={() => onViewRow(row)}
                >
                  <Td>{row.user?.name || "-"}</Td>
                  <Td title={row.user?.email || ""}>{row.user?.email || "-"}</Td>
                  <Td>
                    <Flex alignItems="center" gap="4px">
                      <Tag size="Small" variant="Tinted" color={meta.color}>
                        {meta.label}
                      </Tag>
                      {row.isComp && (
                        <Tag size="Small" variant="Tinted" color="Purple">
                          무료제공
                        </Tag>
                      )}
                    </Flex>
                  </Td>
                  <Td>{row.planName || row.planCode || "-"}</Td>
                  <Td>{row.isComp ? "-" : formatKrw(row.amount)}</Td>
                  <Td>{row.nextBillingAt ? formatKstDate(row.nextBillingAt) : "-"}</Td>
                  <Td>{formatKstDate(row.createdAt)}</Td>
                </Tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrapper>

      {(isLoadingMore || hasMore) && (
        <LoadMoreContainer>
          {isLoadingMore ? (
            <Spinner />
          ) : (
            <Button
              buttonType="button"
              color="Secondary"
              size="Small"
              type="Text"
              variant="Outlined"
              onClick={onLoadMore}
            >
              더 보기
            </Button>
          )}
        </LoadMoreContainer>
      )}
    </TableContainer>
  );
};

export default SubscriptionTable;
